import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "custom/LayoutContainers/DashboardLayout";
import {useCallback, useEffect, useState} from "react";
import {
  addGuardian,
  deleteGuardian,
  getAllGuardians,
  updateGuardian,
} from "../../api/guardian/guardianApiClient";
import GuardianTable from "./components/GuardianTable";
import SoftInput from "../../components/SoftInput";
import {Outlet, useLocation, useNavigate} from "react-router-dom";
import {IconButton, Select, MenuItem, CircularProgress} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import AddGuardianDialog from "./components/AddGuardianDialog";
import SoftButton from "../../components/SoftButton";
import EnhancedTable from "../../components/EnhancedTable";
import {getStatusColor, getStatusDisplayName, getUserRoleColor, getRoleDisplayName} from "../../utils/statusUtils";
import DeleteConfirmDialog from "../users/components/DeleteConfirmDialog";
import {handleApiError, showDeleteSuccess} from "../../utils/notificationUtils";
import {getAllBranches} from "../../api/branch/branchApiClient";

function Overview() {
    const [guardians, setGuardians] = useState([]);
    const [filteredGuardians, setFilteredGuardians] = useState([]);
    const [addEditDialogOpen, setAddEditDialogOpen] = useState(false);
    const [selectedGuardian, setSelectedGuardian] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [guardianToDelete, setGuardianToDelete] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Branch filter state
    const [branches, setBranches] = useState([]);
    const [selectedBranchId, setSelectedBranchId] = useState(null);
    const [isLoadingBranches, setIsLoadingBranches] = useState(false);

    const isOnChildRoute = location.pathname.includes('/guardians/') && location.pathname !== '/guardians';

    const columns = [
        {
            name: "first Name",
            value: "firstName",
            align: "left",
            width: "10",
            cell: ""
        },
        {
            name: "Last Name",
            value: "lastName",
            align: "left",
            width: "10",
            cell: ""
        },
        {
            name: "email",
            value: "email",
            align: "left",
            width: "20",
            cell: ""
        },
        {
            name: "phone No",
            value: "phoneNumber",
            align: "left",
            width: "10",
            cell: ""
        },
        {
            name: "branch",
            value: "branchName",
            align: "left",
            width: "10",
            cell: ""
        },
        {name: "first Name", value: "firstName", align: "left", width: "10"},
        {name: "Last Name", value: "lastName", align: "left", width: "10"},
        {name: "email", value: "email", align: "left", width: "20"},
        {name: "phone No", value: "phoneNumber", align: "left", width: "10"},
        {name: "branch", value: "branchName", align: "left", width: "10"},
        {
            name: "position",
            value: "userRole",
            align: "center",
            width: "10",
        },
        {
            name: "status",
            value: "status",
            align: "center",
            width: "10",
        },
        {
            name: "Actions",
            value: "action",
            align: "center",
            width: "10",
            renderCell: (row) => (
                <>
                    <IconButton color="primary" size="small" title="View Details" onClick={() => handleViewDetails(row)}>
                        <VisibilityIcon/>
                    </IconButton>
                    <IconButton color="primary" size="small" title="Edit" onClick={() => handleEditGuardian(row)}>
                        <EditIcon/>
                    </IconButton>
                    <IconButton color="error" size="small" title="Delete" onClick={() => handleDeleteClick(row)}>
                        <DeleteIcon/>
                    </IconButton>
                </>
            ),
        },
    ];

    const loadGuardians = useCallback(async () => {
        try {
            setIsLoading(true);

            const response = await getAllGuardians();
            console.log('All guardians response:', response);
            console.log('All guardians data:', response.data);
            
            // Log first guardian structure
            if (response.data && response.data.length > 0) {
                console.log('First guardian:', response.data[0]);
            }
            
            setGuardians(response.data);
            setFilteredGuardians(response.data);
        } catch (error) {
            console.error('Error loading guardians:', error);
            handleApiError(error, 'Failed to load guardians. Please try again.');
            setGuardians([]);
            setFilteredGuardians([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Load branches on mount
    useEffect(() => {
        const loadBranches = async () => {
            try {
                setIsLoadingBranches(true);
                const response = await getAllBranches();
                setBranches(Array.isArray(response.data) ? response.data : []);
            } catch (error) {
                console.error('Error loading branches:', error);
            } finally {
                setIsLoadingBranches(false);
            }
        };
        loadBranches();
    }, []);

    useEffect(() => {
        loadGuardians().then();
    }, [loadGuardians]);

    const handleFilterGuardian = (value, branchIdOverride = null) => {
        const branchIdToUse = branchIdOverride !== null ? branchIdOverride : selectedBranchId;
        
        console.log('Filtering guardians with value:', value, 'Branch filter:', branchIdToUse);
        console.log('Guardians array length:', guardians.length);
        
        // Log first guardian to see the structure
        if (guardians.length > 0) {
            console.log('First guardian data:', guardians[0]);
            console.log('BranchId type:', typeof guardians[0].branchId, 'BranchId value:', guardians[0].branchId);
            console.log('SelectedBranchId type:', typeof branchIdToUse, 'SelectedBranchId value:', branchIdToUse);
        }
        
        let filtered = guardians.filter(guardian => {
            const matchesText = guardian.firstName.toLowerCase().includes(value.toLowerCase()) ||
                guardian.lastName.toLowerCase().includes(value.toLowerCase()) ||
                guardian.email.toLowerCase().includes(value.toLowerCase()) ||
                guardian.phoneNumber.toLowerCase().includes(value.toLowerCase());
            
            // Handle both string and number comparison
            const matchesBranch = branchIdToUse ? (String(guardian.branchId) === String(branchIdToUse)) : true;
            
            console.log(`Guardian ${guardian.firstName}: matchesText=${matchesText}, matchesBranch=${matchesBranch} (branchId: ${guardian.branchId} vs ${branchIdToUse})`);
            
            return matchesText && matchesBranch;
        });
        
        console.log('Filtered guardians count:', filtered.length);
        setFilteredGuardians(filtered);
    }

    const handleAddGuardian = () => {
        setSelectedGuardian(null);
        setEditMode(false);
        setAddEditDialogOpen(true);
    };

    const handleEditGuardian = (guardian) => {
        setSelectedGuardian(guardian);
        setEditMode(true);
        setAddEditDialogOpen(true);
    };

    const handleOnSubmitGuardian = async (formData) => {
        try {
            if (editMode) {
                await updateGuardian(selectedGuardian.id, formData);
            } else {
                await addGuardian(formData);
            }
            await loadGuardians();
            setAddEditDialogOpen(false);
            setSelectedGuardian(null);
            setEditMode(false);
        } catch (error) {
            console.error('Error saving guardian:', error);
            // Re-throw so the dialog component can handle API errors
            // and display consistent notification-based messages
            throw error;
        }
    };

    const handleDeleteClick = (guardian) => {
        setGuardianToDelete(guardian);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
      try {
        await deleteGuardian(guardianToDelete.id);
        await loadGuardians();
        setDeleteDialogOpen(false);
        setGuardianToDelete(null);
        showDeleteSuccess("Guardian");
      } catch (error) {
        console.error("Error deleting guardian:", error);
        handleApiError(error, "Failed to delete guardian. Please try again.");
      }
    };

    const handleDeleteCancel = () => {
      setDeleteDialogOpen(false);
      setGuardianToDelete(null);
    };

    const handleViewDetails = (guardian) => {
      navigate(`/guardians/${guardian.id}`);
    };

    return (
      <DashboardLayout>
        <SoftBox pt={2} px={2} width="100%">
          <SoftBox
            pt={2}
            px={2}
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
            mb={2}
          >
            <SoftTypography variant="h4" fontWeight="bold" color="text.primary">
              Care Member Guardian
            </SoftTypography>
          </SoftBox>
          {!isOnChildRoute && (
            <SoftBox
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
              mb={2}
              width="100%"
            >
              <SoftBox
                display="flex"
                alignItems="center"
                gap={2}
                width="100%"
                justifyContent="center"
                maxWidth="1200px"
              >
                <SoftBox width="200px">
                  <SoftTypography variant="caption" fontWeight="bold" color="text.secondary">
                    Branch
                  </SoftTypography>
                  <Select
                    value={selectedBranchId || ""}
                    onChange={(e) => {
                      const newBranchId = e.target.value;
                      setSelectedBranchId(newBranchId);
                      handleFilterGuardian("", newBranchId); // Pass new branchId directly
                    }}
                    displayEmpty
                    fullWidth
                    size="small"
                    disabled={isLoadingBranches}
                  >
                    <MenuItem value="">
                      <em>All Branches</em>
                    </MenuItem>
                    {isLoadingBranches ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Loading...
                      </MenuItem>
                    ) : branches.length === 0 ? (
                      <MenuItem disabled>No branches available</MenuItem>
                    ) : (
                      branches.map((branch) => (
                        <MenuItem key={branch.branchId} value={branch.branchId}>
                          {branch.location || branch.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </SoftBox>
                {selectedBranchId && (
                  <SoftButton
                    size="small"
                    color="primary"
                    onClick={() => {
                      setSelectedBranchId(null);
                      handleFilterGuardian("", null);
                    }}
                  >
                    Clear Filter
                  </SoftButton>
                )}
              </SoftBox>
              <SoftBox
                display="flex"
                alignItems="center"
                gap={2}
                width="100%"
                justifyContent="center"
                maxWidth="1200px"
              >
                <SoftBox pr={3} width="50%">
                  <SoftInput
                    placeholder="Type here to filter by name, email or phone"
                    icon={{ component: "search", direction: "left" }}
                    onChange={(event) => handleFilterGuardian(event.target.value)}
                  />
                </SoftBox>
                <SoftButton color="info" onClick={handleAddGuardian}>
                  Add Guardian
                </SoftButton>
              </SoftBox>
            </SoftBox>
          )}
        </SoftBox>
        <SoftBox p={2} width="100%">
          {isOnChildRoute ? (
            <Outlet />
          ) : (
            <EnhancedTable
              TableComponent={GuardianTable}
              isLoading={isLoading}
              data={filteredGuardians}
              loadingMessage="Loading guardians..."
              noDataMessage="No guardians found"
              columns={columns}
              handleDeleteClick={handleDeleteClick}
              handleEditClick={handleEditGuardian}
              handleViewDetails={handleViewDetails}
            />
          )}
        </SoftBox>
        <AddGuardianDialog
          open={addEditDialogOpen}
          onClose={() => setAddEditDialogOpen(false)}
          guardian={selectedGuardian}
          isEdit={editMode}
          onSubmit={handleOnSubmitGuardian}
        />
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          onDelete={handleDeleteConfirm}
          warningMessage={`Are you sure you want to delete ${guardianToDelete?.firstName || ""} ${
            guardianToDelete?.lastName || ""
          }? This action cannot be undone and will remove the guardian and all their relationships with children.`}
        />
      </DashboardLayout>
    );
}
export default Overview;
